import React, { useState } from 'react';
import styled from '@emotion/styled';
import OrganizationContainer from '../../state/containers/OrganizationContainer';
import MediaContainer from '../../state/containers/MediaContainer';
import { FormikProps } from 'formik';
import Button from '../../components/general/Button';
import TextField from '../../components/forms/TextField';
import PageError from '../../components/general/PageError';
import Switch from '../../components/forms/Switch';
import OrganizationAvatarUpload from '../../components/general/OrganizationAvatarUpload';
import { css } from '@emotion/css';

export interface OrganizationInformationFormProps {
    orgContainer: OrganizationContainer
    mediaContainer: MediaContainer
}

export interface OrganizationInformationFormData {
    name?: string
    isMultiLingual?: boolean
    imageUri?: string
}

const OrganizationInformationForm: React.FC<OrganizationInformationFormProps & FormikProps<OrganizationInformationFormData>> = (props) => {

    const isLoading = props.orgContainer.state.isUpdating;
    const currentOrganization = props.orgContainer.state.currentOrganization;

    const [avatarUploadErrors, setAvatarUploadErrors] = useState<string[]>([]);
    const [descriptionError, setDescriptionError] = useState<string[]>([]);

    const clearAvatarUploadErrors = () => {
        setAvatarUploadErrors([]);
    };

    const clearDescriptionError = () => {
        setDescriptionError([]);
    };

    const isMissingDescription = !currentOrganization?.description;

    return (
        <form onSubmit={props.handleSubmit}>
            <PageError
                errors={avatarUploadErrors}
                onClear={clearAvatarUploadErrors}
            />
            <PageError
                errors={descriptionError}
                onClear={clearDescriptionError}
            />
            {isMissingDescription && (
                <DescriptionWarning>
                    A description is required to update organization settings. Please add a description at <a href="https://app.voicify.com" target="_blank" rel="noopener noreferrer">app.voicify.com</a>
                </DescriptionWarning>
            )}
            <TextField
                name="name"
                disabled={isLoading}
                required
                value={props.values.name}
                label="Organization Name"
                labelStyle={labelStyle}
                customRequiredLabelStyle={customRequiredLabelStyle}
                placeholder="What's the name of your organization?"
                onChange={props.handleChange}
                onBlur={props.handleBlur}
            />
            <OrganizationAvatarUpload
                organizationId={currentOrganization?.id}
                organizationName={currentOrganization?.name}
                currentImageUri={currentOrganization?.imageUri}
                orgContainer={props.orgContainer}
                mediaContainer={props.mediaContainer}
                size="small"
                showLabel={true}
                showHint={true}
                isLoading={props.orgContainer.state.isLoadingOrgs}
                onError={setAvatarUploadErrors}
                onClearError={clearAvatarUploadErrors}
            />
            <SwitchSection>
                <Switch
                    name="isMultiLingual"
                    checked={props.values.isMultiLingual || false}
                    onChange={(checked) => props.setFieldValue('isMultiLingual', checked)}
                    label="Supports Multiple Languages"
                    labelStyle={switchLabelStyle}
                    disabled={isLoading}
                />
            </SwitchSection>
            <Button
                loading={isLoading}
                disabled={isLoading || isMissingDescription}
                themes={["primary"]}
                text="Save"
                type="submit"
            />
        </form>
    )
}

const SwitchSection = styled.div`
    margin: 16px 16px 24px 0px;
`;

const DescriptionWarning = styled.div`
    background: #fff3cd;
    border: 1px solid #ffc107;
    border-radius: 8px;
    padding: 12px 16px;
    margin-bottom: 16px;
    color: #856404;
    font-size: 14px;
    line-height: 1.5;
    a {
        color: #856404;
        font-weight: bold;
        text-decoration: underline;
    }
`;

const switchLabelStyle = css`
    font-size: 14px;
    font-weight: 600;
`;

const labelStyle = css`
    font-size: 14px;
    font-weight: 600;
`;

const customRequiredLabelStyle = css`
    font-size: 14px;
    color: red;
    margin-left: 2px;
    font-weight: 600;
`;

export default OrganizationInformationForm
